import { useState } from "react";
import { ApiClientError, setAdStatus, setGoogleAdStatus } from "../api/client";
import { ConfirmDialog } from "./ConfirmDialog";
import { StateBanner } from "./StateBanner";
import { Badge } from "./Badge";
import { campaignStatusMeta, googleAdTypeLabel, googleApprovalLabel, yen } from "../lib/meta";
import type { CampaignAd } from "../types/contract";

export type AdsFetchState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ok"; data: CampaignAd[] };

interface CampaignAdsPanelProps {
  state: AdsFetchState;
  onRetry: () => void;
  /** pause/activate成功後に再取得したい時に呼ばれる。 */
  onChanged: () => void;
}

/** Meta標準CTAタイプの日本語ラベル。未知の値はそのままコードを表示する（隠さない）。 */
const CTA_LABELS: Record<string, string> = {
  SHOP_NOW: "今すぐ購入",
  LEARN_MORE: "詳しくはこちら",
  SIGN_UP: "登録する",
  SUBSCRIBE: "購読する",
  DOWNLOAD: "ダウンロード",
  GET_OFFER: "オファーを見る",
  MESSAGE_PAGE: "メッセージを送る",
  CONTACT_US: "お問い合わせ",
  APPLY_NOW: "今すぐ申し込む",
  BOOK_TRAVEL: "予約する",
  WATCH_MORE: "続きを見る",
  GET_QUOTE: "見積もりを取得",
  ORDER_NOW: "今すぐ注文",
};

function ctaLabel(cta: string | null): string | null {
  if (!cta) return null;
  return CTA_LABELS[cta] ?? cta;
}

/** カード先頭に出す代表画像。carouselは先頭カードの画像、それ以外はimage_url→thumbnail_urlの順。 */
function primaryImageOf(ad: CampaignAd): string | null {
  if (ad.creative.media_type === "carousel") {
    return ad.creative.carousel_cards[0]?.image_url ?? ad.creative.thumbnail_url;
  }
  return ad.creative.image_url ?? ad.creative.thumbnail_url;
}

/** 着地URL（LP・utm付き）。carouselは先頭カードのlinkを優先し、無ければdestination_url。 */
function landingUrlOf(ad: CampaignAd): string | null {
  if (ad.creative.media_type === "carousel") {
    return ad.creative.carousel_cards[0]?.link ?? ad.creative.destination_url;
  }
  return ad.creative.destination_url;
}

/** 一覧表示用に短縮する: パス＋utm_content（無ければパスのみ）。解析不能な文字列はそのまま返す。 */
function formatLandingUrl(url: string): string {
  try {
    const u = new URL(url);
    const utmContent = u.searchParams.get("utm_content");
    return utmContent ? `${u.pathname} · utm_content=${utmContent}` : u.pathname;
  } catch {
    return url;
  }
}

/** 読込中スケルトンのカード枚数（実データ数が分かる前のプレースホルダ）。 */
const SKELETON_COUNT = 3;

export function CampaignAdsPanel({ state, onRetry, onChanged }: CampaignAdsPanelProps) {
  const [pendingAction, setPendingAction] = useState<{ ad: CampaignAd; action: "pause" | "activate" } | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [expandedText, setExpandedText] = useState<Record<string, boolean>>({});
  const [copyFailed, setCopyFailed] = useState<Record<string, boolean>>({});

  const copyLandingUrl = async (adId: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyFailed((s) => ({ ...s, [adId]: false }));
    } catch {
      setCopyFailed((s) => ({ ...s, [adId]: true }));
    }
  };

  const runAction = async () => {
    if (!pendingAction) return;
    setBusy(true);
    setActionError(null);
    try {
      // Google 広告（"<adGroupId>~<adId>" / "ag_<id>"）は専用エンドポイント。Meta は従来通り ad_id。
      if (pendingAction.ad.platform === "google") await setGoogleAdStatus(pendingAction.ad.id, pendingAction.action);
      else await setAdStatus(pendingAction.ad.id, pendingAction.action);
      setPendingAction(null);
      onChanged();
    } catch (e) {
      setActionError(e instanceof ApiClientError ? e.message : "操作に失敗しました。");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="ads-panel-title">配信中の広告</div>

      {state.status === "loading" && (
        <div className="ads-grid">
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <div key={i} className="ad-card ad-card-skeleton skeleton" />
          ))}
        </div>
      )}

      {state.status === "error" && <StateBanner kind="error" message={state.message} onRetry={onRetry} />}

      {state.status === "ok" && state.data.length === 0 && (
        <div className="empty-state">配信中の広告はありません。</div>
      )}

      {state.status === "ok" && state.data.length > 0 && (
        <div className="ads-grid">
          {state.data.map((ad) => {
            const stMeta = campaignStatusMeta(ad.status);
            const isActive = ad.status === "ACTIVE";
            const textExpanded = !!expandedText[ad.id];
            const cta = ctaLabel(ad.creative.cta);
            const image = primaryImageOf(ad);
            const landingUrl = landingUrlOf(ad);
            const isGoogle = ad.platform === "google";
            const approval = isGoogle ? googleApprovalLabel(ad.google?.approval_status) : null;

            return (
              <div className="ad-card" key={ad.id}>
                {/* Google 広告はテキスト広告/アセットグループのため画像枠を持たない（Meta のみ媒体枠を描画） */}
                {!isGoogle && (
                  <div className="ad-card-media">
                    {image ? (
                      <img src={image} alt="" loading="lazy" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="ad-card-media-placeholder">画像なし</div>
                    )}
                    {ad.creative.media_type === "video" && <span className="ad-media-badge">▶ 動画</span>}
                    {ad.creative.media_type === "carousel" && ad.creative.carousel_cards.length > 0 && (
                      <span className="ad-media-badge">全{ad.creative.carousel_cards.length}枚</span>
                    )}
                  </div>
                )}
                <div className="ad-card-body">
                  <div className="ad-card-header">
                    <span className="ad-card-name">{ad.name}</span>
                    <span className="badge-stack">
                      <Badge label={stMeta.label} color={stMeta.c} bg={stMeta.bg} />
                      {approval && <Badge label={approval} color="var(--warn)" bg="var(--warn-bg)" />}
                    </span>
                  </div>
                  {ad.adset_name && <div className="ad-card-adset">{ad.adset_name}</div>}
                  {isGoogle && <div className="gads-type">{googleAdTypeLabel(ad.google?.ad_type)}</div>}
                  {isGoogle ? (
                    <>
                      {ad.creative.headlines.length > 0 && (
                        <div className="gads-headlines">
                          {ad.creative.headlines.map((h, i) => (
                            <span className="gads-headline" key={i}>
                              {h}
                            </span>
                          ))}
                        </div>
                      )}
                      {ad.creative.primary_texts.length > 0 && (
                        <ul className="gads-descriptions">
                          {ad.creative.primary_texts.map((d, i) => (
                            <li key={i}>{d}</li>
                          ))}
                        </ul>
                      )}
                      {ad.creative.headlines.length === 0 && ad.creative.primary_texts.length === 0 && (
                        <div className="ad-card-adset">
                          {ad.google?.kind === "asset_group"
                            ? "アセット（画像/動画/テキスト）の内訳はGoogle広告の管理画面で確認"
                            : "テキスト要素なし（商品フィード連動）"}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {ad.creative.headlines[0] && <div className="ad-card-headline">{ad.creative.headlines[0]}</div>}
                      {ad.creative.primary_texts[0] && (
                        <p
                          className={`ad-card-text${textExpanded ? " expanded" : ""}`}
                          onClick={() => setExpandedText((s) => ({ ...s, [ad.id]: !s[ad.id] }))}
                          title="クリックで全文表示"
                        >
                          {ad.creative.primary_texts[0]}
                        </p>
                      )}
                      {cta && <span className="ad-card-cta">{cta}</span>}
                    </>
                  )}

                  <div className="ad-card-metrics">
                    <span>
                      消化額 <b>{yen(ad.spend)}</b>
                    </span>
                    <span>
                      CTR <b>{ad.ctr}</b>
                    </span>
                    <span>
                      CPA <b>{ad.cpa != null ? yen(ad.cpa) : "—"}</b>
                    </span>
                    <span>
                      imp <b>{ad.impressions.toLocaleString("ja-JP")}</b>
                    </span>
                    <span>
                      クリック <b>{ad.clicks.toLocaleString("ja-JP")}</b>
                    </span>
                    <span>
                      {ad.primary.label} <b>{ad.primary.value != null ? yen(ad.primary.value) : "—"}</b>
                      {ad.primary.count > 0 && <> （{ad.primary.count.toLocaleString("ja-JP")}件）</>}
                    </span>
                    <span>
                      購入額 <b>{ad.purchase_value > 0 ? yen(ad.purchase_value) : "—"}</b>
                    </span>
                    <span>
                      ROAS <b>{ad.roas != null ? `${ad.roas.toFixed(2)}x` : "—"}</b>
                    </span>
                    {/* 2026-09-15 B′: 楽天CAPI RakutenPurchase（カスタムCV）。標準purchaseとは別集計・全チャネル共通 */}
                    <span>
                      楽天購入 <b>{ad.rakuten_purchases > 0 ? `${ad.rakuten_purchases.toLocaleString("ja-JP")}件` : "—"}</b>
                    </span>
                    <span>
                      楽天購入額 <b>{ad.rakuten_purchase_value > 0 ? yen(ad.rakuten_purchase_value) : "—"}</b>
                    </span>
                  </div>

                  {(ad.creative.preview_link || ad.creative.instagram_permalink) && (
                    <div className="ad-card-links">
                      {ad.creative.preview_link && (
                        <a href={ad.creative.preview_link} target="_blank" rel="noopener noreferrer">
                          Metaプレビュー
                        </a>
                      )}
                      {ad.creative.instagram_permalink && (
                        <a href={ad.creative.instagram_permalink} target="_blank" rel="noopener noreferrer">
                          IG投稿
                        </a>
                      )}
                    </div>
                  )}

                  <div className="ad-card-landing">
                    {landingUrl ? (
                      <>
                        <a
                          className="ad-card-landing-url"
                          href={landingUrl}
                          title={landingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {formatLandingUrl(landingUrl)}
                        </a>
                        <button
                          type="button"
                          className="ad-card-copy-btn"
                          onClick={() => void copyLandingUrl(ad.id, landingUrl)}
                        >
                          コピー
                        </button>
                        {copyFailed[ad.id] && <span className="ad-card-copy-failed">コピー失敗</span>}
                      </>
                    ) : (
                      <span className="ad-card-landing-none">着地URLなし</span>
                    )}
                  </div>

                  <div className="ad-card-toggle-row">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={isActive}
                      aria-label={`${ad.name} の配信を${isActive ? "一時停止" : "再開"}`}
                      disabled={!ad.operable}
                      title={ad.operable ? undefined : isGoogle ? "操作対象外（許可されたキャンペーン外）" : "操作対象外（許可された広告セット外）"}
                      className={`ad-toggle${isActive ? " on" : " off"}`}
                      onClick={() => {
                        if (!ad.operable) return;
                        setActionError(null);
                        setPendingAction({ ad, action: isActive ? "pause" : "activate" });
                      }}
                    >
                      <span className="ad-toggle-knob" />
                    </button>
                    <span className="ad-toggle-label">{isActive ? "配信中" : "一時停止"}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {pendingAction && (
        <ConfirmDialog
          title={pendingAction.action === "pause" ? "配信を一時停止しますか？" : "配信を再開しますか？"}
          description={`「${pendingAction.ad.name}」の配信ステータスを${
            pendingAction.action === "pause" ? "一時停止" : "配信中"
          }に変更します。`}
          confirmLabel={pendingAction.action === "pause" ? "一時停止する" : "再開する"}
          danger={pendingAction.action === "pause"}
          onConfirm={runAction}
          onCancel={() => setPendingAction(null)}
          busy={busy}
          errorMessage={actionError}
        />
      )}
    </div>
  );
}
